import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Select, MenuItem, FormControl, InputLabel, Tabs, Tab
} from '@mui/material';
import { useSnackbar } from 'notistack';
import usuarioService, { Usuario } from '../../services/usuarioService';
import { useAuth } from '../../contexts/AuthContext';
import CajasUsuarioTab from '../../components/sistema/CajasUsuarioTab';

interface UsuarioFormData {
  usuario: string;
  nombre: string;
  password: string;
  email: string;
  role: string;
}

const defaultForm: UsuarioFormData = {
  usuario: '',
  nombre: '',
  password: '',
  email: '',
  role: 'User',
};

const baseRoles = ['Admin', 'User', 'Vendedor', 'Supervisor', 'Operador'];

const UsuarioForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { usuario: usuarioParam } = useParams();
  const [formData, setFormData] = useState<UsuarioFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  // Solo mostrar SUPERADMIN si el usuario actual es SUPERADMIN
  const isSuperAdmin = user?.roles?.includes('SUPERADMIN');
  const availableRoles = isSuperAdmin ? [...baseRoles, 'SUPERADMIN'] : baseRoles;

  const loadUsuario = useCallback(async () => {
    if (!usuarioParam || usuarioParam === 'nuevo') return;
    try {
      setLoading(true);
      const usuario = await usuarioService.getUsuario(usuarioParam);
      console.log('Usuario cargado:', usuario);
      setFormData({
        usuario: (usuario as any).usuario || (usuario as any).Usuario || (usuario as any).id || (usuario as any).Id || '',
        nombre: (usuario as any).username || (usuario as any).Username || (usuario as any).nombre || (usuario as any).Nombre || '',
        password: '',
        email: usuario.email || '',
        role: usuario.role || 'User',
      });
      setIsEdit(true);
    } catch (error) {
      enqueueSnackbar('Error al cargar el usuario', { variant: 'error' });
      navigate('/sistema/usuarios');
    } finally {
      setLoading(false);
    }
  }, [usuarioParam, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (usuarioParam && usuarioParam !== 'nuevo') {
      loadUsuario();
    }
  }, [usuarioParam, loadUsuario]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.usuario.trim()) {
        enqueueSnackbar('El ID de usuario es requerido', { variant: 'warning' });
        return;
      }

      if (!formData.nombre.trim()) {
        enqueueSnackbar('El nombre es requerido', { variant: 'warning' });
        return;
      }

      if (!formData.email.trim()) {
        enqueueSnackbar('El email es requerido', { variant: 'warning' });
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        enqueueSnackbar('El email no es válido', { variant: 'warning' });
        return;
      }

      // Validar que solo SUPERADMIN puede asignar rol SUPERADMIN
      if (formData.role.toUpperCase() === 'SUPERADMIN' && !isSuperAdmin) {
        enqueueSnackbar('Solo usuarios con rol SUPERADMIN pueden asignar este rol', { variant: 'error' });
        return;
      }

      if (!isEdit && (!formData.password || formData.password.length < 6)) {
        enqueueSnackbar('La contraseña debe tener al menos 6 caracteres', { variant: 'warning' });
        return;
      }

      const payload: Usuario = {
        id: formData.usuario.trim(),
        usuario: formData.usuario.trim(),
        username: formData.nombre.trim(),
        nombre: formData.nombre.trim(),
        email: formData.email.trim(),
        role: formData.role,
        ...(formData.password && { password: formData.password })
      };

      if (isEdit) {
        await usuarioService.updateUsuario(usuarioParam!, payload);
      } else {
        await usuarioService.createUsuario(payload);
      }

      enqueueSnackbar('Usuario guardado exitosamente', { variant: 'success' });
      navigate('/sistema/usuarios');
    } catch (error: any) {
      console.error('Error al guardar usuario:', error);
      const errorMessage = error?.response?.data?.message || 'Error al guardar el usuario';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {isEdit ? 'Editar Usuario' : 'Nuevo Usuario'}
          </Typography>

          <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Grupos de Caja" disabled={!isEdit} />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="usuario" 
                  label="Usuario" 
                  value={formData.usuario} 
                  onChange={handleChange} 
                  fullWidth 
                  required 
                  disabled={isEdit}
                  helperText="ID único del usuario (sin espacios)"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="nombre" 
                  label="Nombre" 
                  value={formData.nombre} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  helperText="Nombre completo del usuario"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="email" 
                  label="Email" 
                  type="email"
                  value={formData.email} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Rol</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    label="Rol"
                    sx={{ borderRadius: 2 }}
                  >
                    {availableRoles.map(role => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="password" 
                  label={isEdit ? "Nueva Contraseña (dejar vacío para no cambiar)" : "Contraseña"}
                  type="password"
                  value={formData.password} 
                  onChange={handleChange} 
                  fullWidth 
                  required={!isEdit}
                  helperText={isEdit ? "Dejar vacío si no desea cambiar la contraseña" : "Mínimo 6 caracteres"}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
            )}

            {currentTab === 1 && (
              <CajasUsuarioTab
                usuario={formData.usuario}
                username={formData.nombre}
                disabled={!isEdit}
              />
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/sistema/usuarios')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UsuarioForm;
